import React, { useState, useEffect } from 'react';
import { Award, Trophy, Flame, RefreshCw, AlertCircle, ArrowLeft } from 'lucide-react';
import API_BASE_URL from '../config';
import { maskName, maskPhone } from '../utils/utils';

const LeaderboardView = ({ gymId, isAdmin, onBack }) => {
  const [month, setMonth] = useState('current');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const shouldMask = (member) => {
    if (!member) return false;
    const currentUser = JSON.parse(localStorage.getItem("fitscan_user") || "{}");
    const isSelf = member.memberId === currentUser.id || member.id === currentUser.id || member.username?.toLowerCase() === currentUser.username?.toLowerCase();
    return !isSelf;
  };

  const getDisplayPhone = (member) => {
    if (!member) return '';
    let phone = shouldMask(member) ? (member.maskedPhone || maskPhone(member.username)) : member.username;
    if (phone && phone.startsWith('@')) {
      phone = phone.slice(1);
    }
    return phone;
  };

  const getDisplayName = (member) => {
    if (!member) return '';
    return shouldMask(member) ? (member.maskedName || maskName(member.displayName)) : member.displayName;
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError('');
    try {
      let token = '';
      if (isAdmin) {
        token = localStorage.getItem('admin_token');
      } else {
        const tokenObj = JSON.parse(localStorage.getItem('accessToken') || '{}');
        token = tokenObj.token || '';
      }

      const response = await fetch(`${API_BASE_URL}/api/Leaderboard/${gymId}?month=${month}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve consistency leaderboard.');
      }

      const result = await response.json();
      if (result.success) {
        setData(result);
      } else {
        setError(result.message || 'An error occurred while generating the leaderboard.');
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to API to fetch leaderboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gymId) {
      fetchLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymId, month]);

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Get current and last month display names
  const getCurrentMonthName = () => {
    const date = new Date();
    if (month === 'last') {
      date.setMonth(date.getMonth() - 1);
    }
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const renderPodium = (podiumList) => {
    // Arrange as: 2nd (left), 1st (center), 3rd (right)
    const first = podiumList.find(m => m.rank === 1);
    const second = podiumList.find(m => m.rank === 2);
    const third = podiumList.find(m => m.rank === 3);

    const podiumOrder = [];
    if (second) podiumOrder.push({ ...second, color: 'border-slate-400 bg-slate-500/10 text-slate-300', shadow: 'shadow-slate-500/10', height: 'h-48' });
    if (first) podiumOrder.push({ ...first, color: 'border-yellow-500 bg-yellow-500/10 text-yellow-300', shadow: 'shadow-yellow-500/20', height: 'h-56 scale-105 z-10' });
    if (third) podiumOrder.push({ ...third, color: 'border-amber-700 bg-amber-700/10 text-amber-500', shadow: 'shadow-amber-700/10', height: 'h-40' });

    // If fewer than 3, just sort them by rank to show what's available
    const displayList = podiumList.length < 3 
      ? [...podiumList].sort((a, b) => a.rank - b.rank).map(p => {
          if (p.rank === 1) return { ...p, color: 'border-yellow-500 bg-yellow-500/10 text-yellow-300', shadow: 'shadow-yellow-500/20', height: 'h-56' };
          if (p.rank === 2) return { ...p, color: 'border-slate-400 bg-slate-500/10 text-slate-300', shadow: 'shadow-slate-500/10', height: 'h-48' };
          return { ...p, color: 'border-amber-700 bg-amber-700/10 text-amber-500', shadow: 'shadow-amber-700/10', height: 'h-40' };
        })
      : podiumOrder;

    return (
      <div className="flex flex-col sm:flex-row items-end justify-center gap-6 sm:gap-2 md:gap-6 py-8">
        {displayList.map((member) => (
          <div 
            key={member.memberId} 
            className={`w-full sm:w-44 md:w-52 rounded-2xl border flex flex-col justify-between p-5 text-center shadow-2xl transition-all duration-300 hover:scale-[1.03] ${member.color} ${member.shadow} ${member.height}`}
          >
            {/* Medal header */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black/40 border border-white/10 font-bold text-xs uppercase tracking-wide">
                #{member.rank}
              </div>
              <div className={`w-14 h-14 rounded-full flex items-center justify-center border-2 text-lg font-bold shadow-inner bg-black/40 ${member.color}`}>
                {member.photoUrl ? (
                  <img src={member.photoUrl} alt={getDisplayName(member)} className="w-full h-full rounded-full object-cover" />
                ) : (
                  getInitials(getDisplayName(member))
                )}
              </div>
              <div>
                <h4 className="font-bold text-white text-sm line-clamp-1">{getDisplayName(member)}</h4>
                <p className="text-gray-500 text-xs font-mono lowercase">@{getDisplayPhone(member)}</p>
              </div>
            </div>

            {/* Attendance stats */}
            <div className="bg-black/30 border border-white/5 rounded-xl p-2.5 mt-4 space-y-1">
              <div className="flex justify-between text-xs text-gray-400">
                <span>Score:</span>
                <span className="font-bold text-white">{member.consistencyScore}%</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>Streak:</span>
                <span className="font-bold text-orange-400 flex items-center gap-0.5">
                  <Flame size={12} className="fill-orange-400" />
                  {member.currentStreak}d
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Visits:</span>
                <span>{member.attendanceDaysThisMonth} days</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* View Header with back button (if provided) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {onBack && (
            <button 
              onClick={onBack} 
              className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Award className="text-lime-400" />
              Consistency Leaderboard
            </h2>
            <p className="text-gray-400 text-xs mt-0.5">{getCurrentMonthName()}</p>
          </div>
        </div>

        {/* Tab switch controller */}
        <div className="flex bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl p-1 w-fit">
          <button
            onClick={() => setMonth('current')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all uppercase tracking-wide ${
              month === 'current'
                ? 'bg-lime-400 text-[#0d0d0d]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Current Month
          </button>
          <button
            onClick={() => setMonth('last')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all uppercase tracking-wide ${
              month === 'last'
                ? 'bg-lime-400 text-[#0d0d0d]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Last Month (Top 3)
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center py-20 text-lime-400 gap-3">
          <RefreshCw size={28} className="animate-spin" />
          <span className="text-sm font-medium text-gray-400">Loading standings...</span>
        </div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl p-5 flex items-center gap-3">
          <AlertCircle size={20} />
          {error}
        </div>
      ) : data?.isPlaceholder ? (
        <div className="text-center py-16 px-4 bg-[#161616] border border-[#2b2b2b] border-dashed rounded-2xl space-y-3">
          <Trophy size={48} className="mx-auto text-gray-600 animate-bounce" />
          <p className="text-sm text-gray-400 max-w-sm mx-auto">{data.message}</p>
        </div>
      ) : (
        <div className="space-y-8 animate-fadeIn">
          {/* Top 3 Podium section */}
          {data?.podium && data.podium.length > 0 && renderPodium(data.podium)}

          {/* Detailed Leaderboard list for Ranks 4-10 (only show for current month) */}
          {month === 'current' && data?.rankings && data.rankings.length > 0 && (
            <div className="bg-[#161616] border border-[#2b2b2b] rounded-2xl overflow-hidden shadow-xl">
              <div className="px-5 py-4 border-b border-[#2b2b2b] bg-[#0d0d0d]">
                <h3 className="text-xs font-bold text-white uppercase tracking-wider">Top 10 Rankings</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-300">
                  <thead className="text-xs text-gray-400 uppercase bg-[#0d0d0d] border-b border-[#2b2b2b]">
                    <tr>
                      <th scope="col" className="px-5 py-3 text-center w-16">Rank</th>
                      <th scope="col" className="px-5 py-3">Member</th>
                      <th scope="col" className="px-5 py-3 text-center">Score</th>
                      <th scope="col" className="px-5 py-3 text-center">Active Streak</th>
                      <th scope="col" className="px-5 py-3 text-center">Visits</th>
                      <th scope="col" className="px-5 py-3 text-center">Lifetime Checkins</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2b2b2b]">
                    {data.rankings.map((member) => (
                      <tr key={member.memberId} className="hover:bg-white/5 transition-colors">
                        <td className="px-5 py-4 text-center font-mono font-bold text-[#6CD800]">
                          #{member.rank}
                        </td>
                        <td className="px-5 py-4 flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full border border-[#2b2b2b] flex items-center justify-center font-bold text-xs bg-[#0d0d0d] text-gray-300">
                            {member.photoUrl ? (
                              <img src={member.photoUrl} alt={getDisplayName(member)} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              getInitials(getDisplayName(member))
                            )}
                          </div>
                          <div>
                            <span className="block font-semibold text-white">{getDisplayName(member)}</span>
                            <span className="block text-xs text-gray-500 font-mono">@{getDisplayPhone(member)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-center font-semibold text-white">
                          {member.consistencyScore}%
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-500/10 text-orange-400 border border-orange-500/20">
                            <Flame size={12} className="fill-orange-400" />
                            {member.currentStreak} Days
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center text-xs text-gray-400">
                          {member.attendanceDaysThisMonth} Days
                        </td>
                        <td className="px-5 py-4 text-center font-mono text-xs text-gray-500">
                          {member.lifetimeCheckins}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaderboardView;
